import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobRole } from '~/shared/data-contract/job-role';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { TableBody, Table as TableComponent, TableHeader, TableHeaderCell } from '~/ui/core/table/Table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/ui/core/tabs/Tabs';
import { Tile, TileContent, TileControlGroup, TileStickyHeader } from '~/ui/core/tile/Tile';

import { useArchivedItems } from '../api/use-archived-items';
import { ContentContainer } from '../components/Common';
import { useActiveJobArchitectureId } from '../url-navigation/job-architecture-scope';
import { ARCHIVED_OPTIONAL_COLUMN_CLASS, ArchivedItemRow, useArchivedItemsColumnWidths } from './ArchivedItemRow';
import { ArchivedItemsTableSkeleton } from './ArchivedItemsTableSkeleton';

type ArchivedItemsPanelProps = {
    readonly onClose: () => void;
};

const SimpleHeaderContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.m,
    justifyContent: 'flex-start',
    width: '100%',
}));

const ContentHeaderContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xl,
    justifyContent: 'space-between',
    paddingBottom: theme.spacing['4xl'],
}));

const HeaderTitleSection = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.s,
}));

const Title = styled.h1(({ theme }) => ({
    ...theme.typography.body.L.semibold,
    color: theme.colors.gray[900],
    margin: 0,
}));

const Subtitle = styled.p(({ theme }) => ({
    ...theme.typography.body.S.regular,
    color: theme.colors.gray[600],
    margin: 0,
}));

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const TabsContainer = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xl,
    paddingBottom: theme.spacing['4xl'],
    width: '100%',
}));

const EmptyStateText = styled.div(({ theme }) => ({
    ...theme.typography.body.S.regular,
    color: theme.colors.gray[600],
    padding: theme.spacing.xl,
    textAlign: 'center',
}));

const ErrorText = styled.div(({ theme }) => ({
    ...theme.typography.body.S.regular,
    color: theme.colors.error[600],
    padding: theme.spacing.xl,
    textAlign: 'center',
}));

const StyledTabsContent = styled(TabsContent)(({ theme }) => ({
    marginTop: theme.spacing.xl,
    padding: 0,
}));

type HeaderProps = ArchivedItemsPanelProps;

const Header: FunctionComponent<HeaderProps> = ({ onClose }) => {
    return (
        <TileStickyHeader>
            <SimpleHeaderContainer>
                <TileControlGroup>
                    <Button aria-label="close" iconOnly={true} size="sm" variant="secondary" onClick={onClose}>
                        <IconCrossSmall />
                    </Button>
                </TileControlGroup>
            </SimpleHeaderContainer>
        </TileStickyHeader>
    );
};

type LoaderProps = ArchivedItemsPanelProps & {
    readonly title: string;
};

const Loader: FunctionComponent<LoaderProps> = ({ onClose, title }) => {
    const translate = useTranslate();

    return (
        <Tile data-testid="archive-loader">
            <ScrollArea>
                <TileContent>
                    <ContentContainer>
                        <Container>
                            <Header onClose={onClose} />
                            <ContentHeaderContainer>
                                <HeaderTitleSection>
                                    <Title>{title}</Title>
                                    <Subtitle>
                                        <SkeletonBase className="h-l w-[12rem]" />
                                    </Subtitle>
                                </HeaderTitleSection>
                            </ContentHeaderContainer>
                            <TabsContainer>
                                <Tabs defaultValue="roles">
                                    <TabsList>
                                        <TabsTrigger disabled={true} value="roles">
                                            {translate('pages.job-architecture-v2.archived-roles')} (…)
                                        </TabsTrigger>
                                        <TabsTrigger disabled={true} value="levels">
                                            {translate('pages.job-architecture-v2.archived-levels')} (…)
                                        </TabsTrigger>
                                    </TabsList>
                                    <StyledTabsContent value="roles">
                                        <ArchivedItemsTableSkeleton />
                                    </StyledTabsContent>
                                </Tabs>
                            </TabsContainer>
                        </Container>
                    </ContentContainer>
                </TileContent>
            </ScrollArea>
        </Tile>
    );
};

type ErrorStateProps = ArchivedItemsPanelProps & {
    readonly errorMessage: string;
    readonly title: string;
};

const ErrorState: FunctionComponent<ErrorStateProps> = ({ errorMessage, onClose, title }) => {
    return (
        <Tile data-testid="archive-error">
            <ScrollArea>
                <TileContent>
                    <ContentContainer>
                        <Container>
                            <Header onClose={onClose} />
                            <ContentHeaderContainer>
                                <HeaderTitleSection>
                                    <Title>{title}</Title>
                                </HeaderTitleSection>
                            </ContentHeaderContainer>
                            <ErrorText>{errorMessage}</ErrorText>
                        </Container>
                    </ContentContainer>
                </TileContent>
            </ScrollArea>
        </Tile>
    );
};

type ArchivedRolesListProps = {
    readonly emptyMessage: string;
    readonly jobArchitectureId: string;
    readonly roles: readonly JobRole[];
};

type ArchivedListHeaderProps = {
    readonly translate: (key: string) => string;
};

const ArchivedListHeader: FunctionComponent<ArchivedListHeaderProps> = ({ translate }) => {
    const columnWidths = useArchivedItemsColumnWidths();

    return (
        <TableHeader $columnWidths={columnWidths}>
            <TableHeaderCell>{translate('pages.job-architecture-v2.table-column.name')}</TableHeaderCell>
            <TableHeaderCell>{translate('pages.job-architecture-v2.table-column.type')}</TableHeaderCell>
            <TableHeaderCell>{translate('pages.job-architecture-v2.table-column.status')}</TableHeaderCell>
            <TableHeaderCell className={ARCHIVED_OPTIONAL_COLUMN_CLASS}>
                {translate('pages.job-architecture-v2.table-column.reviewer')}
            </TableHeaderCell>
            <TableHeaderCell className={ARCHIVED_OPTIONAL_COLUMN_CLASS}>
                {translate('pages.job-architecture-v2.table-column.published-by')}
            </TableHeaderCell>
            <TableHeaderCell />
        </TableHeader>
    );
};

const ArchivedItemsTable: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => (
    <TableComponent aria-label="Archived items" style={{ height: 'auto' }}>
        {children}
    </TableComponent>
);

const ArchivedRolesList: FunctionComponent<ArchivedRolesListProps> = ({ emptyMessage, jobArchitectureId, roles }) => {
    const translate = useTranslate();

    if (is.emptyArray(roles)) {
        return <EmptyStateText>{emptyMessage}</EmptyStateText>;
    }

    return (
        <ArchivedItemsTable>
            <ArchivedListHeader translate={translate} />
            <TableBody>
                {roles.map((role) => (
                    <ArchivedItemRow key={role.id} item={role} jobArchitectureId={jobArchitectureId} type="role" />
                ))}
            </TableBody>
        </ArchivedItemsTable>
    );
};

type ArchivedLevelsListProps = {
    readonly emptyMessage: string;
    readonly jobArchitectureId: string;
    readonly levels: readonly JobLevel[];
};

const ArchivedLevelsList: FunctionComponent<ArchivedLevelsListProps> = ({
    emptyMessage,
    jobArchitectureId,
    levels,
}) => {
    const translate = useTranslate();

    if (is.emptyArray(levels)) {
        return <EmptyStateText>{emptyMessage}</EmptyStateText>;
    }

    return (
        <ArchivedItemsTable>
            <ArchivedListHeader translate={translate} />
            <TableBody>
                {levels.map((level) => (
                    <ArchivedItemRow key={level.id} item={level} jobArchitectureId={jobArchitectureId} type="level" />
                ))}
            </TableBody>
        </ArchivedItemsTable>
    );
};

const getSubtitleKey = (totalCount: number): TranslationKey =>
    totalCount === 1
        ? 'pages.job-architecture-v2.archived-items-subtitle'
        : 'pages.job-architecture-v2.archived-items-subtitle-plural';

type ArchivedItemsContentProps = ArchivedItemsPanelProps & {
    readonly archivedLevels: readonly JobLevel[];
    readonly archivedRoles: readonly JobRole[];
    readonly jobArchitectureId: string;
};

const ArchivedItemsContent: FunctionComponent<ArchivedItemsContentProps> = ({
    archivedLevels,
    archivedRoles,
    jobArchitectureId,
    onClose,
}) => {
    const translate = useTranslate();
    const totalCount = archivedRoles.length + archivedLevels.length;
    const subtitleKey = getSubtitleKey(totalCount);
    const title = translate('pages.job-architecture-v2.archived-items');

    return (
        <Tile data-testid="archive-panel">
            <ScrollArea>
                <TileContent>
                    <ContentContainer>
                        <Container>
                            <Header onClose={onClose} />
                            <ContentHeaderContainer>
                                <HeaderTitleSection>
                                    <Title>{title}</Title>
                                    <Subtitle>{translate(subtitleKey, { count: totalCount.toString() })}</Subtitle>
                                </HeaderTitleSection>
                            </ContentHeaderContainer>
                            <TabsContainer>
                                <Tabs defaultValue="roles">
                                    <TabsList>
                                        <TabsTrigger value="roles">
                                            {translate('pages.job-architecture-v2.archived-roles')} (
                                            {archivedRoles.length})
                                        </TabsTrigger>
                                        <TabsTrigger value="levels">
                                            {translate('pages.job-architecture-v2.archived-levels')} (
                                            {archivedLevels.length})
                                        </TabsTrigger>
                                    </TabsList>
                                    <StyledTabsContent value="roles">
                                        <ArchivedRolesList
                                            emptyMessage={translate('pages.job-architecture-v2.no-archived-roles')}
                                            jobArchitectureId={jobArchitectureId}
                                            roles={archivedRoles}
                                        />
                                    </StyledTabsContent>
                                    <StyledTabsContent value="levels">
                                        <ArchivedLevelsList
                                            emptyMessage={translate('pages.job-architecture-v2.no-archived-levels')}
                                            jobArchitectureId={jobArchitectureId}
                                            levels={archivedLevels}
                                        />
                                    </StyledTabsContent>
                                </Tabs>
                            </TabsContainer>
                        </Container>
                    </ContentContainer>
                </TileContent>
            </ScrollArea>
        </Tile>
    );
};

export const ArchivedItemsPanel: FunctionComponent<ArchivedItemsPanelProps> = ({ onClose }) => {
    const translate = useTranslate();
    const activeJobArchitectureId = useActiveJobArchitectureId();
    const { data, isError, isLoading } = useArchivedItems(activeJobArchitectureId);

    const title = translate('pages.job-architecture-v2.archived-items');

    if (isLoading) {
        return <Loader title={title} onClose={onClose} />;
    }

    if (isError) {
        return (
            <ErrorState
                errorMessage={translate('pages.job-architecture-v2.an-error-occurred')}
                title={title}
                onClose={onClose}
            />
        );
    }

    const archivedRoles = data?.archivedRoles ?? [];
    const archivedLevels = data?.archivedLevels ?? [];

    return (
        <ArchivedItemsContent
            archivedLevels={archivedLevels}
            archivedRoles={archivedRoles}
            jobArchitectureId={activeJobArchitectureId}
            onClose={onClose}
        />
    );
};
